import React from "react";
import ReactDOM from "react-dom";

import { CalendarIcon, CloseIcon } from "../../lib/icons";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function toISO(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fromISO(s) {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function formatDisplay(d) {
  if (!d) return "";
  return `${MONTHS[d.getMonth()].slice(0, 3)} ${d.getDate()}, ${d.getFullYear()}`;
}

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d, n) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function sameDay(a, b) {
  return a && b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function isBetween(d, a, b) {
  if (!d || !a || !b) return false;
  const t = d.getTime();
  return t > a.getTime() && t < b.getTime();
}

function getPresetRange(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const y = today.getFullYear();
  const m = today.getMonth();
  const d = today.getDate();
  const day = today.getDay();

  switch (preset) {
    case "today":
      return [new Date(y, m, d), new Date(y, m, d)];
    case "yesterday": {
      const yd = new Date(y, m, d - 1);
      return [yd, yd];
    }
    case "this_week": {
      const start = new Date(y, m, d - day);
      const end = new Date(y, m, d - day + 6);
      return [start, end];
    }
    case "last_week": {
      const start = new Date(y, m, d - day - 7);
      const end = new Date(y, m, d - day - 1);
      return [start, end];
    }
    case "this_month":
      return [new Date(y, m, 1), new Date(y, m + 1, 0)];
    case "last_month":
      return [new Date(y, m - 1, 1), new Date(y, m, 0)];
    case "this_quarter": {
      const qStart = Math.floor(m / 3) * 3;
      return [new Date(y, qStart, 1), new Date(y, qStart + 3, 0)];
    }
    case "last_quarter": {
      const qStart = Math.floor(m / 3) * 3 - 3;
      return [new Date(y, qStart, 1), new Date(y, qStart + 3, 0)];
    }
    case "this_year":
      return [new Date(y, 0, 1), new Date(y, 11, 31)];
    case "last_year":
      return [new Date(y - 1, 0, 1), new Date(y - 1, 11, 31)];
    case "last_7":
      return [new Date(y, m, d - 6), new Date(y, m, d)];
    case "last_30":
      return [new Date(y, m, d - 29), new Date(y, m, d)];
    case "last_90":
      return [new Date(y, m, d - 89), new Date(y, m, d)];
    case "last_180":
      return [new Date(y, m, d - 179), new Date(y, m, d)];
    case "last_365":
      return [new Date(y, m, d - 364), new Date(y, m, d)];
    default:
      return [null, null];
  }
}

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This Week" },
  { key: "last_week", label: "Last Week" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "this_quarter", label: "This Quarter" },
  { key: "last_quarter", label: "Last Quarter" },
  { key: "this_year", label: "This Year" },
  { key: "last_year", label: "Last Year" },
];

const QUICK_OPTIONS = [
  { key: "last_7", label: "Last 7 Days" },
  { key: "last_30", label: "Last 30 Days" },
  { key: "last_90", label: "Last 90 Days" },
  { key: "last_180", label: "Last 180 Days" },
  { key: "last_365", label: "Last 365 Days" },
];

function Calendar({ viewDate, start, end, onSelect, hoverDate, onHover }) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const total = daysInMonth(year, month);

  const cells = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let d = 1; d <= total; d += 1) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const rangeEnd = end || hoverDate;

  return (
    <div className="drm-calendar">
      <div className="drm-calendar-title">{MONTHS[month]} {year}</div>
      <div className="drm-calendar-grid">
        {DOW.map((d) => (
          <div key={d} className="drm-dow">{d}</div>
        ))}
        {cells.map((cell, i) => {
          if (!cell) return <div key={`e-${i}`} />;
          const isStart = sameDay(cell, start);
          const isEnd = sameDay(cell, end);
          const inRange = start && rangeEnd && isBetween(cell, start, rangeEnd);
          const inHoverRange = start && !end && hoverDate && isBetween(cell, start, hoverDate);
          let cls = "drm-day";
          if (isStart || isEnd) cls += " drm-day-edge";
          if (inRange || inHoverRange) cls += " drm-day-range";
          return (
            <button
              key={cell.toISOString()}
              type="button"
              className={cls}
              onClick={() => onSelect(cell)}
              onMouseEnter={() => onHover(cell)}
            >
              {cell.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DateRangeModal({ open, startValue, endValue, onApply, onClose }) {
  const initialStart = fromISO(startValue);
  const initialEnd = fromISO(endValue);
  const [start, setStart] = React.useState(initialStart);
  const [end, setEnd] = React.useState(initialEnd);
  const [hoverDate, setHoverDate] = React.useState(null);
  const [viewDate, setViewDate] = React.useState(() => startOfMonth(initialStart || new Date()));
  const [activePreset, setActivePreset] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setStart(fromISO(startValue));
      setEnd(fromISO(endValue));
      setHoverDate(null);
      setActivePreset(null);
      setViewDate(startOfMonth(fromISO(startValue) || new Date()));
    }
  }, [open, startValue, endValue]);

  if (!open) return null;

  function pickDay(d) {
    setActivePreset(null);
    if (!start || (start && end)) {
      setStart(d);
      setEnd(null);
      return;
    }
    if (d < start) {
      setEnd(start);
      setStart(d);
    } else {
      setEnd(d);
    }
  }

  function applyPreset(key) {
    const [s, e] = getPresetRange(key);
    setStart(s);
    setEnd(e);
    setActivePreset(key);
    if (s) setViewDate(startOfMonth(s));
  }

  function handleApply() {
    if (!start) {
      onApply("", "");
    } else {
      onApply(toISO(start), toISO(end || start));
    }
  }

  function handleClear() {
    setStart(null);
    setEnd(null);
    setHoverDate(null);
    setActivePreset(null);
  }

  const rightMonth = addMonths(viewDate, 1);

  return ReactDOM.createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div className="drm-panel" onClick={(e) => e.stopPropagation()}>
        <div className="drm-header">
          <div className="drm-header-title">
            <CalendarIcon />
            <h2>Select Date Range</h2>
          </div>
          <button type="button" className="close-button" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="drm-body">
          <aside className="drm-presets">
            <div className="drm-section-label">Presets</div>
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`drm-preset-btn${activePreset === p.key ? " active" : ""}`}
                onClick={() => applyPreset(p.key)}
              >
                <CalendarIcon />
                <span>{p.label}</span>
              </button>
            ))}
          </aside>

          <div className="drm-center">
            <div className="drm-range-display">
              <CalendarIcon />
              <span>{formatDisplay(start) || "Start date"}</span>
              <span className="drm-range-sep">–</span>
              <span>{formatDisplay(end) || "End date"}</span>
            </div>
            <div className="drm-calendars">
              <button
                type="button"
                className="drm-nav"
                onClick={() => setViewDate(addMonths(viewDate, -1))}
                aria-label="Previous month"
              >
                ‹
              </button>
              <Calendar
                viewDate={viewDate}
                start={start}
                end={end}
                hoverDate={hoverDate}
                onSelect={pickDay}
                onHover={setHoverDate}
              />
              <Calendar
                viewDate={rightMonth}
                start={start}
                end={end}
                hoverDate={hoverDate}
                onSelect={pickDay}
                onHover={setHoverDate}
              />
              <button
                type="button"
                className="drm-nav"
                onClick={() => setViewDate(addMonths(viewDate, 1))}
                aria-label="Next month"
              >
                ›
              </button>
            </div>
          </div>

          <aside className="drm-quick">
            <div className="drm-section-label">Quick Options</div>
            {QUICK_OPTIONS.map((q) => (
              <button
                key={q.key}
                type="button"
                className={`drm-quick-btn${activePreset === q.key ? " active" : ""}`}
                onClick={() => applyPreset(q.key)}
              >
                {q.label}
              </button>
            ))}
            <div className="drm-section-label" style={{ marginTop: 16 }}>Selected Range</div>
            <div className="drm-selected-field">
              <div className="drm-selected-label">Start Date</div>
              <div className="drm-selected-value">
                <CalendarIcon />
                <span>{formatDisplay(start) || "—"}</span>
              </div>
            </div>
            <div className="drm-selected-field">
              <div className="drm-selected-label">End Date</div>
              <div className="drm-selected-value">
                <CalendarIcon />
                <span>{formatDisplay(end) || "—"}</span>
              </div>
            </div>
          </aside>
        </div>

        <div className="drm-footer">
          <button type="button" className="drm-clear" onClick={handleClear}>
            Clear Filter
          </button>
          <div className="drm-footer-actions">
            <button type="button" className="drm-cancel" onClick={onClose}>Cancel</button>
            <button type="button" className="drm-apply" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default DateRangeModal;
